import { MouseEvent, useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useNavigate } from 'react-router-dom'
import commonActions from '../../../actions/common.actions'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import WidgetIcon from '../../../assets/icons/widget'
import ROUTES from '../../../assets/routes'
import useSelectPrimaryOrUnderlyingSecurity from '../../../hooks/useSelectPrimaryOrUnderlyingSecurity'
import { Platform } from '../../../proto/generated/user-preference'
import { replaceWidget } from '../../../reducer/widgetDashboard.reducer'
import { updateChartConfig } from '../../../reducer/chart.reducer'
import { selectIsMobile } from '../../../selectors/device'
import { selectActiveLayoutId, selectLastActiveLayoutId } from '../../../selectors/home'
import { selectDefaultLayoutId } from '../../../selectors/user'
import { selectAllLayouts } from '../../../selectors/widgetDashboard'
import { AppDispatch } from '../../../store'
import { isEmbeddedModeSet } from '../../../util/general'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../custom'
import { WidgetTypes, WidgetTypesNames } from '../../widgetDashboard/widgetDashboard.enums'
import LayoutSlotPickerDialog from '../../widgetDashboard/layoutSlotPicker/LayoutSlotPickerDialog'
import { ChartConfig } from '../../chartV2/chart.typings'
import { getDefaultChartConfig } from '../../../util/chart'

export type AddToLayoutButtonVariant = 'bar' | 'chartHover'

interface AddToLayoutButtonProps {
    className?: string
    widgetType?: WidgetTypes
    variant?: AddToLayoutButtonVariant
    /** Smaller control for overview / preview tiles. */
    compact?: boolean
    source?: string
    chartConfig?: Partial<ChartConfig>
}

const widgetLabel = (widgetType: WidgetTypes) =>
    WidgetTypesNames[widgetType]?.toLowerCase() ?? 'widget'

const AddToLayoutButton = ({
    className,
    widgetType = WidgetTypes.OPTION_CHAIN,
    variant = 'bar',
    compact = false,
    source,
    chartConfig,
}: AddToLayoutButtonProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const lastActiveLayoutId = useSelector(selectLastActiveLayoutId)
    const isMobile = useSelector(selectIsMobile)
    const defaultLayoutId = useSelector(selectDefaultLayoutId(isMobile ? Platform.MOBILE : Platform.DESKTOP))
    const layouts = useSelector(selectAllLayouts)
    const { primaryOrUnderlying, primaryOrUnderlyingToken } = useSelectPrimaryOrUnderlyingSecurity()
    const [dialogOpen, setDialogOpen] = useState(false)
    const [selectedSlotId, setSelectedSlotId] = useState<number | null>(null)
    const eventSource = source ?? `${widgetType.toLowerCase()}-bar`
    const isHover = variant === 'chartHover'

    const layoutId = useMemo(() => {
        if (activeLayoutId && layouts[activeLayoutId]) return activeLayoutId
        if (lastActiveLayoutId && layouts[lastActiveLayoutId]) return lastActiveLayoutId
        if (defaultLayoutId && layouts[defaultLayoutId]) return defaultLayoutId
        return ''
    }, [activeLayoutId, defaultLayoutId, lastActiveLayoutId, layouts])
    const layout = layoutId ? layouts[layoutId] : undefined
    const widgetSlotIds = useMemo(
        () => Object.keys(layout?.widgets ?? {}).map(Number),
        [layout?.widgets]
    )
    const disabled = !layout || widgetSlotIds.length === 0 || !primaryOrUnderlyingToken || !primaryOrUnderlying?.hasFo
    const tooltipTitle = !layout
        ? 'No widget layout is available'
        : widgetSlotIds.length === 0
            ? 'The active layout has no widgets to replace'
            : !primaryOrUnderlying?.hasFo
                ? 'F&O is not available for this security'
                : `Add ${widgetLabel(widgetType)} to layout`

    const applyToSlot = useCallback((slotId: number) => {
        if (!layoutId || !primaryOrUnderlyingToken || !primaryOrUnderlying) return

        const chartId = `${layoutId}-${slotId}`
        dispatch(replaceWidget({
            id: layoutId,
            slotId,
            widgetType,
            settings: {
                key: primaryOrUnderlyingToken,
                expressionMode: false,
                securities: [primaryOrUnderlyingToken],
                displayName: primaryOrUnderlying.displayName ?? primaryOrUnderlying.symbol,
                chartId,
            },
        }))
        if (chartConfig) {
            dispatch(updateChartConfig({
                viewKey: chartId,
                config: {
                    ...getDefaultChartConfig(),
                    ...chartConfig,
                },
            }))
        }
        AnalyticsManager.logEvent('Add to Layout', {
            source: eventSource,
            widgetType,
        })
        commonActions.setApiError(dispatch, {
            error: true,
            message: `${WidgetTypesNames[widgetType]} added to layout`,
            errorType: 'success',
        })
        setDialogOpen(false)
        setSelectedSlotId(null)
        if (!isEmbeddedModeSet()) {
            navigate(generatePath(ROUTES.chart, { layoutId }))
        }
    }, [chartConfig, dispatch, eventSource, layoutId, navigate, primaryOrUnderlying, primaryOrUnderlyingToken, widgetType])

    const handleClick = useCallback((event?: MouseEvent) => {
        event?.stopPropagation()
        event?.preventDefault()
        if (disabled) return
        if (widgetSlotIds.length === 1) {
            applyToSlot(widgetSlotIds[0])
            return
        }
        setSelectedSlotId(null)
        setDialogOpen(true)
    }, [applyToSlot, disabled, widgetSlotIds])

    const handleClose = useCallback(() => {
        setDialogOpen(false)
        setSelectedSlotId(null)
    }, [])

    return (
        <>
            <NonInteractiveTooltip title={tooltipTitle} arrow placement="top">
                <span
                    className={isHover ? 'add-to-layout-hover' : undefined}
                    style={isHover ? { display: 'inline-flex' } : undefined}
                    onClick={isHover ? (event) => event.stopPropagation() : undefined}
                    onMouseDown={isHover ? (event) => event.stopPropagation() : undefined}
                    onKeyDown={isHover ? (event) => event.stopPropagation() : undefined}
                >
                    <ExtraSmallButton
                        className={className}
                        color="inherit"
                        disabled={disabled}
                        startIcon={<WidgetIcon sx={{ fontSize: compact ? 14 : 18 }} />}
                        onClick={handleClick}
                        data-event="Add to layout clicked"
                        data-event-source={eventSource}
                        sx={{
                            px: compact ? 0.75 : 1,
                            ...(compact
                                ? {
                                    height: 22,
                                    minHeight: 22,
                                    maxHeight: 22,
                                    minWidth: 0,
                                    py: 0,
                                    fontSize: 11,
                                    lineHeight: 1.2,
                                    '& .MuiButton-startIcon': {
                                        mr: 0.5,
                                        ml: 0,
                                    },
                                }
                                : {}),
                            ...(isHover
                                ? {
                                    bgcolor: 'background.paper',
                                    boxShadow: 1,
                                    '&:hover': { bgcolor: 'background.paper' },
                                }
                                : {}),
                        }}
                    >
                        Add to layout
                    </ExtraSmallButton>
                </span>
            </NonInteractiveTooltip>
            {layout && (
                <LayoutSlotPickerDialog
                    open={dialogOpen}
                    panes={layout.panes}
                    widgets={layout.widgets}
                    selectedSlotId={selectedSlotId}
                    onSelect={setSelectedSlotId}
                    onClose={handleClose}
                    onConfirm={() => {
                        if (selectedSlotId !== null) applyToSlot(selectedSlotId)
                    }}
                />
            )}
        </>
    )
}

export default AddToLayoutButton
